import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { authUrl, PHOTO_CATEGORIES } from "../../lib/re";
import { toast } from "sonner";
import { UploadCloud, Star, Trash2, RefreshCw, Maximize2, ChevronLeft, ChevronRight, X, FileText, Video, GripVertical } from "lucide-react";

function Lightbox({ items, index, onClose, onMove }) {
  const m = items[index];
  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="lightbox">
      <button className="absolute top-4 right-4 text-slate-300 hover:text-white" onClick={onClose} data-testid="lightbox-close"><X size={28} /></button>
      <button className="absolute left-3 text-slate-300 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); onMove(-1); }} data-testid="lightbox-prev"><ChevronLeft size={36} /></button>
      <figure className="max-w-6xl max-h-[88vh] px-14" onClick={(e) => e.stopPropagation()}>
        {m.kind === "video" ? <video src={authUrl(m.url)} controls className="max-h-[80vh]" /> : <img src={authUrl(m.url)} alt={m.caption} className="max-h-[80vh] max-w-full object-contain mx-auto" />}
        <figcaption className="text-center text-sm text-slate-300 mt-3">{m.caption || m.category} · {index + 1}/{items.length}</figcaption>
      </figure>
      <button className="absolute right-3 text-slate-300 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); onMove(1); }} data-testid="lightbox-next"><ChevronRight size={36} /></button>
    </div>
  );
}

function Thumb({ m, i, editable, onCover, onDelete, onReplace, onMeta, onZoom, drag }) {
  const isCover = i === 0 && m.kind === "foto";
  return (
    <div draggable={editable} onDragStart={() => drag.start(m.id)} onDragOver={(e) => { e.preventDefault(); drag.over(m.id); }} onDrop={drag.drop}
      data-testid={`media-item-${m.id}`} className={`card-almar overflow-hidden group ${drag.overId === m.id ? "ring-2 ring-[#C9A84C]" : ""}`}>
      <div className="aspect-[4/3] relative bg-[#0A1622] cursor-zoom-in" onClick={() => m.kind !== "planta" ? onZoom() : window.open(authUrl(m.url), "_blank")}>
        {m.kind === "foto" && <img src={authUrl(m.url)} alt={m.caption} loading="lazy" className="w-full h-full object-cover" />}
        {m.kind === "video" && <div className="w-full h-full flex items-center justify-center text-[#C9A84C]"><Video size={36} /></div>}
        {m.kind === "planta" && (m.content_type || "").startsWith("image") && <img src={authUrl(m.url)} alt="Planta" className="w-full h-full object-contain bg-white" />}
        {m.kind === "planta" && !(m.content_type || "").startsWith("image") && <div className="w-full h-full flex flex-col items-center justify-center text-[#C9A84C]"><FileText size={36} /><span className="text-xs mt-1">Planta PDF</span></div>}
        {isCover && <span className="absolute top-2 left-2 text-[10px] bg-[#C9A84C] text-[#0D1B2A] font-bold px-2 py-0.5 rounded" data-testid="cover-badge">PRINCIPAL</span>}
        <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded p-1"><Maximize2 size={14} className="text-white" /></span>
        {editable && <span className="absolute bottom-2 left-2 bg-black/60 rounded p-1 cursor-grab"><GripVertical size={14} className="text-white" /></span>}
      </div>
      <div className="p-2 space-y-1.5">
        <input disabled={!editable} defaultValue={m.caption} placeholder="Legenda" className="input-almar py-1 text-xs" data-testid={`media-caption-${m.id}`}
          onBlur={(e) => e.target.value !== (m.caption || "") && onMeta({ caption: e.target.value })} />
        <select disabled={!editable} value={m.category || "Geral"} className="input-almar py-1 text-xs" data-testid={`media-category-${m.id}`} onChange={(e) => onMeta({ category: e.target.value })}>
          {PHOTO_CATEGORIES.map((c) => <option key={c}>{c}</option>)}{m.category && !PHOTO_CATEGORIES.includes(m.category) && <option>{m.category}</option>}
        </select>
        {editable && (
          <div className="flex justify-between pt-1">
            {m.kind === "foto" && !isCover ? <button title="Definir como principal" onClick={onCover} data-testid={`media-cover-${m.id}`} className="text-slate-400 hover:text-[#C9A84C] p-1"><Star size={15} /></button> : <span />}
            <div className="flex">
              <button title="Substituir" onClick={onReplace} data-testid={`media-replace-${m.id}`} className="text-slate-400 hover:text-sky-300 p-1"><RefreshCw size={15} /></button>
              <button title="Eliminar" onClick={onDelete} data-testid={`media-delete-${m.id}`} className="text-slate-400 hover:text-red-400 p-1"><Trash2 size={15} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function Gallery({ property, editable, reload }) {
  const media = property.media || [];
  const inputRef = useRef(null);
  const replaceRef = useRef(null);
  const [replacing, setReplacing] = useState(null);
  const [kind, setKind] = useState("foto");
  const [category, setCategory] = useState("Geral");
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [filter, setFilter] = useState("");
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const base = `/re/properties/${property.id}/media`;
  const errMsg = (e) => e?.response?.data?.detail || "Erro ao processar o ficheiro";

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    fd.append("kind", kind); fd.append("category", category);
    try { await api.post(base, fd); toast.success(`${files.length} ficheiro(s) carregado(s)`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const doReplace = async (file) => {
    if (!file || !replacing) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`${base}/${replacing}/replace`, fd); toast.success("Ficheiro substituído"); reload(); } catch (e) { toast.error(errMsg(e)); }
    setReplacing(null);
  };
  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };

  const drag = {
    overId,
    start: (id) => setDragId(id),
    over: (id) => setOverId(id),
    drop: async () => {
      if (!dragId || !overId || dragId === overId) { setOverId(null); return; }
      const ids = media.map((m) => m.id);
      ids.splice(ids.indexOf(dragId), 1);
      ids.splice(ids.indexOf(overId), 0, dragId);
      setDragId(null); setOverId(null);
      await act(() => api.put(`${base}/order`, { ids }), "Ordem atualizada");
    },
  };

  const shown = media.filter((m) => !filter || m.category === filter || m.kind === filter);
  const viewable = shown.filter((m) => m.kind !== "planta" || (m.content_type || "").startsWith("image"));
  const cats = [...new Set(media.map((m) => m.category).filter(Boolean))];

  return (
    <div>
      {editable && (
        <div className="card-almar p-4 mb-5 flex flex-col md:flex-row gap-3 md:items-center">
          <select className="input-almar md:w-40" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="media-kind-select">
            <option value="foto">Fotografias</option><option value="planta">Plantas</option><option value="video">Vídeos</option>
          </select>
          <select className="input-almar md:w-48" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="media-upload-category">
            {PHOTO_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <button onClick={() => inputRef.current?.click()} data-testid="media-upload-btn" disabled={busy}
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
            className="flex-1 border-2 border-dashed border-[#1E3A56] hover:border-[#C9A84C]/60 rounded-lg py-3 text-sm text-slate-300 flex items-center justify-center gap-2 transition-colors">
            <UploadCloud size={18} className="text-[#C9A84C]" /> {busy ? "A carregar..." : "Carregar vários ficheiros (arraste ou clique)"}
          </button>
          <input ref={inputRef} type="file" multiple hidden accept="image/*,video/*,application/pdf" data-testid="media-file-input" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
          <input ref={replaceRef} type="file" hidden accept="image/*,video/*,application/pdf" data-testid="media-replace-input" onChange={(e) => { doReplace(e.target.files[0]); e.target.value = ""; }} />
        </div>
      )}
      {media.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {["", "foto", "planta", "video", ...cats].map((c) => (
            <button key={c || "all"} onClick={() => setFilter(c)} data-testid={`gallery-filter-${c || "all"}`}
              className={`px-3 py-1 rounded-full text-xs border ${filter === c ? "bg-[#C9A84C]/20 border-[#C9A84C]/50 text-[#E0C574]" : "border-[#1E3A56] text-slate-400"}`}>
              {{ "": "Tudo", foto: "Fotografias", planta: "Plantas", video: "Vídeos" }[c] ?? c}
            </button>
          ))}
        </div>
      )}
      {media.length === 0 ? <p className="text-sm text-slate-500 py-10 text-center">Ainda não existem fotografias ou ficheiros multimédia.</p> : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="gallery-grid">
          {shown.map((m) => (
            <Thumb key={m.id + (m.caption || "") + m.category} m={m} i={media.indexOf(m)} editable={editable} drag={drag}
              onZoom={() => setZoom(viewable.indexOf(m))}
              onCover={() => act(() => api.post(`${base}/${m.id}/cover`), "Fotografia principal definida")}
              onDelete={() => window.confirm("Eliminar este ficheiro?") && act(() => api.delete(`${base}/${m.id}`), "Ficheiro eliminado")}
              onReplace={() => { setReplacing(m.id); replaceRef.current?.click(); }}
              onMeta={(d) => act(() => api.patch(`${base}/${m.id}`, d))} />
          ))}
        </div>
      )}
      {zoom != null && zoom >= 0 && viewable[zoom] && (
        <Lightbox items={viewable} index={zoom} onClose={() => setZoom(null)} onMove={(d) => setZoom((zoom + d + viewable.length) % viewable.length)} />
      )}
    </div>
  );
}
